import { useMemo } from "react";
// Material UI
import { GridColDef } from "@mui/x-data-grid-pro";
import { Typography } from "@mui/material";

// Components
import { TypographyHeader } from "@/Layouts/components/Tables/DataSmartTable/additional/TypographyHeader";
import { TypographyCell } from "@/Layouts/components/Tables/DataSmartTable/additional/TypographyCell";

// Constants
import { HEADER_CLASS_NAME } from "@/Constants/tables";

// Helpers
import { getFormatUTCDate } from "@/Common/helpers/Dates";
import { getCellClassNameFn } from "@/Common/helpers/Tables";
import { getPinnedCellClassName, generateRenderingFn, WithTooltip } from "@/Layouts/components/Tables/DataSmartTable/helpers/";

// Types
import { IDeal } from "@/Common/types/MyCit/model";

// Icons
import CircleRoundedIcon from "@mui/icons-material/CircleRounded";
import { Coin } from "../../FniManagement/components/CIT/Coin";
import { GridInitialState } from "@mui/x-data-grid-premium";
import CommonToolbar from "@/Layouts/components/Tables/DataSmartTable/toolbar/CommonToolbar";
import NoRowsOverlay from "@/Layouts/components/Tables/DataSmartTable/additional/NoRowsOverlay";
import { DataSmartTableProps } from "@/Layouts/components/types";
import useAggregationExtended from "@/Layouts/components/Tables/DataSmartTable/aggregation/Aggregation";
import { useMyCitContextData } from "@/DataPages/context/myCit/MyCitContext";
import { DATE_FORMAT } from "@/Constants/date";

const baseConfig: Partial<GridColDef> = {
	align: "right",
	headerClassName: HEADER_CLASS_NAME,
	renderHeader: (params) => <TypographyHeader params={params} right={params.colDef.align === "right"} />,
	cellClassName: getCellClassNameFn(),
	renderCell: generateRenderingFn(),
	resizable: false,
	type: "string",
};

const baseColumnDefinitions: GridColDef[] = [
	{
		field: "_id",
		headerName: "ID",
		width: 10,
		filterable: false,
	},
	{
		headerName: "Funded",
		renderHeader: () => <></>,
		field: "funded",
		width: 52,
		align: "center",
		cellClassName: getPinnedCellClassName,
		type: "singleSelect",
		valueOptions: ["Funded", "Unfunded"],
		valueGetter: (_value, deal: IDeal) => (deal.funded ? "Funded" : "Unfunded"),
		renderCell: (params) => {
			const { value, id } = params;
			if (id === "auto-generated-group-footer-root") {
				return <TypographyCell params={{ ...params, value: "Total", formattedValue: "Total" }}></TypographyCell>;
			}

			return (
				<TypographyCell component="div">
					<Coin active={value === "Funded"} activeText="Funded" inactiveText="Unfunded" />
				</TypographyCell>
			);
		},
	},
	{
		field: "age",
		headerName: "Age",
		type: "number",
		width: 50,
		cellClassName: getPinnedCellClassName,
		renderCell: (params) => {
			const { id } = params;
			if (id === "auto-generated-group-footer-root") {
				return null;
			}
			return <TypographyCell params={params} />;
		},
	},
	{
		field: "date",
		headerName: "Date",
		type: "date",
		align: "left",
		width: 70,
		cellClassName: getPinnedCellClassName,
		valueFormatter: (value) => getFormatUTCDate(value, DATE_FORMAT.usDateSlashShortYear),
	},
	{
		field: "stockNumber",
		headerName: "Stock #",
		width: 80,
		cellClassName: getPinnedCellClassName,
	},
	{
		field: "customerNumber",
		headerName: "Customer #",
		width: 100,
		cellClassName: getPinnedCellClassName,
	},
	{
		field: "dealNumber",
		headerName: "Deal #",
		width: 70,
		align: "right",
		cellClassName: getPinnedCellClassName,
	},
	{
		field: "customerName",
		headerName: "Customer Name",
		align: "left",
		width: 170,
		renderCell: WithTooltip,
		cellClassName: getPinnedCellClassName,
	},
	{
		field: "__empty",
		headerName: "",
		sortable: false,
		resizable: false,
		filterable: false,
		width: 12,
		headerClassName: "!z-10 !bg-white !border-none !p-0",
		cellClassName: () => "empty-row !bg-white !border-none !p-0",
		renderCell: () => <TypographyCell />,
	},
	{
		field: "vinNumber",
		headerName: "VIN (8)",
		width: 130,
		renderCell: WithTooltip,
	},
	{
		field: "lender",
		headerName: "Lender",
		align: "left",
		width: 130,
		renderCell: WithTooltip,
	},
	{
		field: "balance",
		headerName: "Balance",
		type: "number",
		renderHeader: (params) => <TypographyHeader params={params} className="fix-flex-direction" right={false} />,
		width: 120,
		renderCell: generateRenderingFn({
			render: "absolute",
			hints: {
				isMoney: true,
			},
		}),
	},
	{
		field: "amountFinanced",
		headerName: "Financed Amount",
		type: "number",
		renderHeader: (params) => <TypographyHeader params={params} className="fix-flex-direction" right={false} />,
		width: 140,
		renderCell: generateRenderingFn({
			render: "absolute",
			hints: {
				isMoney: true,
			},
		}),
	},
	{
		field: "dealStatus",
		headerName: "STAT",
		align: "left",
		width: 60,
		renderCell: (row) => {
			if (!row.value) {
				return null;
			}
			return (
				<Typography className="regular-pt-sans-pro-narrow-label fs-12" component="p">
					<CircleRoundedIcon
						className={`deal-status-${row.value}`}
						sx={{
							fontSize: "12px",
							marginTop: "-3px",
							marginRight: "5px",
						}}
					/>
					{row.value}
				</Typography>
			);
		},
	},
	{
		field: "salesManager",
		headerName: "Sales Manager",
		align: "left",
		width: 120,
		renderCell: WithTooltip,
	},
	{
		field: "salesEmployee",
		headerName: "Salesperson",
		align: "left",
		width: 110,
		renderCell: WithTooltip,
	},
	{
		field: "trade",
		headerName: "Trade",
		type: "singleSelect",
		valueOptions: ["Has Trade", "No Trade"],
		align: "left",
		width: 70,
	},
];

const pinnedColumns = {
	left: ["funded", "age", "date", "stockNumber", "customerNumber", "dealNumber", "customerName", "__empty"],
};

const slots = {
	toolbar: CommonToolbar,
	noRowsOverlay: NoRowsOverlay,
};

const columnVisibilityModel = {
	_id: false,
};

export const useMyCitTableConfiguration = () => {
	const { filterModel } = useMyCitContextData();

	const initialState = useMemo<GridInitialState>(
		() => ({
			filter: {
				filterModel,
			},
			aggregation: {
				model: {
					balance: "sum",
					amountFinanced: "sum",
					transactionAmount: "sum",
					dealNumber: "uniqueCount",
				},
			},
		}),
		[filterModel]
	);

	const slotProps = useMemo(
		() => ({
			toolbar: {
				titleExpandible: {
					className: "mt-[14px] [&_.toolbar-actions]:!right-0 [&_.toolbar-actions]:!bottom-0",
					hide: true,
				},
				filter: {
					hide: false,
				},
				search: {
					useQuickFilter: true,
					initiallyOpened: Boolean(filterModel.quickFilterValues?.length),
				},
			},
			noRowsOverlay: {
				title: "No Contracts In Transit Data",
			},
		}),
		[filterModel]
	);

	const columns = useMemo<GridColDef[]>(
		() =>
			baseColumnDefinitions.map((customConfig) => ({
				...baseConfig,
				...customConfig,
				minWidth: customConfig.minWidth ?? customConfig.width,
				maxWidth: customConfig.maxWidth ?? customConfig.width,
			})),
		[]
	);

	const aggregations = useAggregationExtended({});

	const customProps = useMemo<DataSmartTableProps["customProps"]>(
		() => ({
			tableContainerClassName: "[&_.MuiDataGrid-main]:mt-2 after:[&_.MuiDataGrid-topContainer]:!bg-disabled",
			tableClassName: {
				"[&_.MuiDataGrid-columnHeader--withRightBorder]:!border-none": true,
				"[&_.MuiDataGrid-filler--pinnedLeft]:!border-r-transparent": true,
				"[&_.tree-grid-child-row:has(+:not(.tree-grid-child-row))_.MuiDataGrid-cell--pinnedLeft:not(.empty-row)]:shadow-table-line":
					true,
				"[&_.MuiDataGrid-row:not(.footer):hover_.MuiDataGrid-cell--pinnedLeft:not(.empty-row,.group-child)]:shadow-table-hov": true,
				"[&_.tree-grid-child-row:has(+:not(.tree-grid-child-row)):hover_.MuiDataGrid-cell--pinnedLeft:not(.empty-row,.group-child)]:shadow-table-line-hov":
					true,
				"[&_.MuiDataGrid-columnHeader--alignRight_.MuiDataGrid-columnHeaderTitleContainer:has(.fix-flex-direction)]:flex-row": true,
			},
			expandableRows: false,
			hide: false,
			customAggregationsFn: aggregations,
		}),
		[]
	);

	return useMemo(
		() => ({ columns, pinnedColumns, slots, columnVisibilityModel, initialState, slotProps, customProps }),
		[columns, customProps, slotProps, initialState]
	);
};
